<template>
  <div class="patient-summary">
    <div class="avatar">
      <figure class="image is-64x64">
        <div
          class="is-profile-picture-64x64"
        >
          <img
            :src="patient.profilePicture || $Utilities.getDefaultProfilePicture(patient.Gender)"
          >
        </div>
      </figure>

      <div class="patient-info">
        <p>
          <strong>{{ patient.name || patient.Name }}</strong>
          <span> <b>{{ patient.RegisterNumber ? '\xa0\xa0- ' + patient.RegisterNumber : '' }}</b></span>
        </p>

        <!-- POC Cuidados Paliativos (Tela 4): resumo do plano de cuidados, visível em todas as abas -->
        <ul
          v-if="carePlanChips.length > 0"
          class="care-plan-strip"
          aria-label="Resumo do plano de cuidados"
        >
          <li
            v-for="chip in carePlanChips"
            :key="chip.key"
            class="care-plan-chip"
            :class="chip.tone ? `is-${chip.tone}` : ''"
          >
            {{ chip.text }}
          </li>
        </ul>

        <div
          v-show="patient.ticketId != null"
          class="urgency-rating"
        >
          <p
            class="rating"
            :class="urgencyRatingClass ? urgencyRatingClass : patient.doctorPriority"
          >
            {{ urgencyRatingName ? urgencyRatingName : getPatientUrgency }}
          </p>
          <button
            type="button"
            class="borderless"
            @click="openReclassifyUrgencyModal()"
          >
            Reclassificar Urgência?
          </button>
        </div>
      </div>
    </div>
      
    <div>
      <p>{{ $Utilities.getAbbrevGender(patient.gender || patient.Gender) }}{{ (patient.birthDate || patient.BirthDate) ? `, ${$Utilities.getAge(patient.birthDate || patient.BirthDate)}` : '' }}</p>
    </div>

    <div class="has-text-centered">
      <p><strong>{{ patient.displayCid || patient.cid }}</strong></p>
    </div>

    <div class="has-text-centered">
      <p v-show="patient.painLevel">
        Dor: <strong>{{ patient.painLevel }}</strong>
      </p>
      <p>
        <strong>{{ patient.doctor }}</strong>
      </p>
    </div>
  </div>

  <div class="reclassify-urgency-modal default-modal">
    <div
      class="modal"
      :class="modalReclassifyUrgencyActive ? 'is-active' : ''"
    >
      <form @submit.prevent="reclassifyUrgency()">
        <div class="modal-background" />

        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Reclassificar Urgência?
            </p>
          </header>

          <section class="modal-card-body">
            <ul>
              <li>
                <label class="custom-radio">
                  <input
                    v-model="tempDoctorPriority"
                    type="radio"
                    name="urgencyClassification"
                    value="High"
                  >
                  <span class="radio-title rating urgent">Urgente</span>
                  <span class="checkmark" />
                </label>
              </li>
              <li>
                <label class="custom-radio">
                  <input
                    v-model="tempDoctorPriority"
                    type="radio"
                    name="urgencyClassification"
                    value="Normal"
                  >
                  <span class="radio-title rating moderate">Moderada</span>
                  <span class="checkmark" />
                </label>
              </li>
              <li>
                <label class="custom-radio">
                  <input
                    v-model="tempDoctorPriority"
                    type="radio"
                    name="urgencyClassification"
                    value="Low"
                  >
                  <span class="radio-title rating low">Baixa</span>
                  <span class="checkmark" />
                </label>
              </li>
            </ul>
          </section>

          <footer class="modal-card-foot">
            <lila-button
              type="submit"
              title="Salvar"
              class="button"
              :loading="changingUrgency"
              :is-centralized="true"
              :second-button="{type: 'button', title: 'Cancelar'}"
              @custom-click-cancel-event="closeReclassifyUrgencyModal()"
            />
          </footer>
        </div>
      </form>
    </div> 
  </div>
</template>

<script>
import { eventBus } from "../../../eventBus.js";
import { ref, provide, onMounted } from "vue";
import LilaButton from "../button/Button.vue";
import {
  CARE_PLAN_UPDATED_EVENT,
  getCarePlan,
  getDeathRecord,
  placeLabel
} from "@/services/palliative/carePlanApi";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";

export default {
  components: {
    LilaButton
  },
  // POC Cuidados Paliativos: plano e óbito salvos em outra aba atualizam as etiquetas.
  mixins: [pocLiveSync("loadCarePlan")],
  props: {
    patient: {
      required: true,
      type: Object
    }
  },
  setup(props){
    const changingUrgency = ref(false);
    const modalReclassifyUrgencyActive = ref(false);
    const tempDoctorPriority = ref("");
    const alert = ref({
      message: "",
      type: "",
      show: false
    });

    provide("alert-message", alert);

    function openTicket(ticketId){
      if(ticketId) {
        props.patient.ticketId = ticketId;
        props.patient.doctorPriority = "moderate";
      }
    }

    onMounted(() => {
      eventBus.on("openTicket", openTicket);
    });


    return { changingUrgency, modalReclassifyUrgencyActive, tempDoctorPriority, alert };
  },
  data() {
    return {
      carePlan: null,
      deathRecord: null
    };
  },
  computed: {
    carePlanPatientId(){
      return this.patient.Id ?? this.patient.id;
    },
    // Faixa: "PPS 40% · Diretiva registrada · Prefere cuidado em casa · Cuidadora: Ana"
    carePlanChips(){
      const plan = this.carePlan;
      const death = this.deathRecord;

      // Tela 6: depois do óbito, a faixa mostra o registro e o acompanhamento do luto.
      if (death) {
        const deathChips = [{
          key: "death",
          text: death.deathPlace === "casa" ? "Óbito em casa"
            : death.deathPlace === "hospital" ? "Óbito no hospital" : "Óbito registrado"
        }];
        if (death.wasDesired === "sim") deathChips.push({ key: "desired", text: "No local desejado", tone: "ok" });
        if (death.wasDesired === "nao") deathChips.push({ key: "desired", text: "Fora do local desejado", tone: "pending" });
        if (death.griefTrail) deathChips.push({ key: "grief", text: "Acompanhamento pós-óbito", tone: "ok" });
        return deathChips;
      }

      if (!plan || !plan.updatedAt) return [];

      const chips = [];
      if (plan.pps) chips.push({ key: "pps", text: `PPS ${plan.pps}%` });
      chips.push(plan.directiveRegistered
        ? { key: "dav", text: "Diretiva registrada", tone: "ok" }
        : { key: "dav", text: "Sem diretiva registrada", tone: "pending" });
      if (plan.carePlace && plan.carePlace !== "indefinido") {
        chips.push({ key: "place", text: `Prefere cuidado ${placeLabel(plan.carePlace).toLowerCase()}` });
      }
      if (plan.caregiverName) chips.push({ key: "caregiver", text: `Cuidadora: ${plan.caregiverName}` });
      return chips;
    },
    urgencyRatingName(){
      switch (this.patient.doctorPriority) {
      case "High":
        return "Urgente";
      case "Normal":
        return "Moderado";
      case "Low":
        return "Baixa";
      default:
        return "";
      }
    },
    urgencyRatingClass(){
      switch (this.patient.doctorPriority) {
        case "High":
          return "urgent";
      case "Normal":
        return "moderate";
      case "Low":
        return "low";
      default:
        return "";
      }
    },
    getPatientUrgency(){
      switch (this.patient.doctorPriority) {
      case "urgent":
        return "Urgente";
      case "moderate":
        return "Moderado";
      case "low":
        return "Baixa";
      default:
        return "";
      }
    }
  },
  watch: {
    "patient": {
      handler: function(){
        this.getServiceLocation();
        this.loadCarePlan();
      }
    }
  },
  created(){
    this.getServiceLocation();
    this.loadCarePlan();
    eventBus.on(CARE_PLAN_UPDATED_EVENT, this.onCarePlanUpdated);
  },
  beforeUnmount(){
    eventBus.off(CARE_PLAN_UPDATED_EVENT, this.onCarePlanUpdated);
  },
  methods: {
    async loadCarePlan(){
      const id = this.$Utilities.hasPalliativeCareAccess(this) ? this.carePlanPatientId : null;
      if (id == null) {
        this.carePlan = null;
        this.deathRecord = null;
        return;
      }
      try {
        const [carePlan, deathRecord] = await Promise.all([getCarePlan(id), getDeathRecord(id)]);
        if (String(id) !== String(this.carePlanPatientId)) return;
        this.carePlan = carePlan;
        this.deathRecord = deathRecord;
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar o plano de cuidados.", error);
      }
    },
    onCarePlanUpdated(payload){
      if (String(payload?.patientId) === String(this.carePlanPatientId)) this.loadCarePlan();
    },

    getPatient(patientId){
      this.$axios
        .get(
          `/api/patient/record/v2/${patientId}`,
          {headers: {"Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}`}}
        )
        .then((res) => {
          if(res.data.Team) this.patient.doctor = res.data.Team.Name;
        });
    },
    getServiceLocation(){
      if(this.patient.doctor == undefined || this.patient.doctor == "") this.getPatient(this.patient.id);
    },
    reclassifyUrgency(){
      this.alert.show = false;
      this.changingUrgency = true;

      this.$axios({
        method: "PUT",
        url: `/api/chat/${this.patient.ticketId}`,
        data: {priority: this.tempDoctorPriority},
        headers: {"Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}`}
      })
        .then(() => {
          this.patient.doctorPriority = this.tempDoctorPriority;

          this.updateTicketPriority(this.patient.doctorPriority, this.patient.id);
        })
        .catch(error => {
          console.error(error);
          this.$Utilities.verifyToken(error.response.status);
          this.$Utilities.controlAlert(error.response.data, this.alert, "danger");
        })
        .finally(() => {
          this.changingUrgency = false;
          this.closeReclassifyUrgencyModal();
        });
    },
    openReclassifyUrgencyModal(){
      this.modalReclassifyUrgencyActive = true;
    },
    closeReclassifyUrgencyModal(){
      this.tempDoctorPriority = this.patient.doctorPriority;
      this.modalReclassifyUrgencyActive = false;
    },
    updateTicketPriority(priority, patientId) {
      let payload = {
        priority: priority,
        patientId: patientId
      };
      eventBus.emit("updateTicketPriority", payload);
    }
  }
};
</script>

<style>
  @import '~@assets/css/components/patientCardSummary.css';

  /* POC Cuidados Paliativos (Tela 4) */
  .care-plan-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 420px;
    margin: 2px 0 0;
    padding: 0;
    list-style: none;
  }

  /* Anula recuo de listas herdado de estilos globais: alinha com o nome */
  .patient-summary .patient-info ul.care-plan-strip {
    margin: 2px 0 0;
    padding: 0;
  }

  .patient-summary .patient-info ul.care-plan-strip li.care-plan-chip {
    margin: 0;
  }

  .care-plan-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #4f3f82;
    background-color: #ece9f5;
  }

  .care-plan-chip.is-ok,
  .care-plan-chip.is-pending {
    font-weight: 700;
  }

  .care-plan-chip.is-ok::before,
  .care-plan-chip.is-pending::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: currentColor;
  }

  .care-plan-chip.is-ok {
    color: #1e6b3a;
    background-color: #e3f4e8;
  }

  .care-plan-chip.is-pending {
    color: #8a5a00;
    background-color: #fff3d6;
  }
</style>
